import apiClient from './api';

export const usuarioService = {
  // Crear un nuevo usuario (registro) - usa endpoint de auth
  createUsuario: async (data) => {
    const payload = {
      name: data.nombre || data.name,
      lastname: data.apellido || data.lastname || '',
      email: data.email || data.correo,
      // Si no se proporciona password explícito, usar el DNI como contraseña por defecto
      password: data.password || data.dni || data.DNI || '',
      dni: data.dni || '',
      rol: data.rol || 'POSTULANTE',
      supervisorId: data.supervisorId || null
    };
    const response = await apiClient.post('/auth/register', payload);
    return response.data;
  },

  // Listar todos los usuarios (requiere autenticación ADMIN/SISTEMAS)
  getAllUsuarios: async () => {
    const response = await apiClient.get('/usuarios');
    return response.data;
  },

  // Obtener un usuario por ID
  getUsuarioById: async (id) => {
    const response = await apiClient.get(`/usuarios/${id}`);
    return response.data;
  },

  // Actualizar un usuario existente
  updateUsuario: async (id, data) => {
    const response = await apiClient.put(`/usuarios/${id}`, data);
    return response.data;
  },

  // Eliminar un usuario
  deleteUsuario: async (id) => {
    const response = await apiClient.delete(`/usuarios/${id}`);
    return response.data;
  },

  // Reenviar credenciales al usuario por email
  resendCredentials: async (id) => {
    const response = await apiClient.post(`/usuarios/${id}/resend-credentials`);
    return response.data;
  },

  // Alias para reenviarCredenciales
  reenviarCredenciales: async (id) => {
    const response = await apiClient.post(`/usuarios/${id}/resend-credentials`);
    return response.data;
  },

  // Cambiar estado de aptitud del usuario
  cambiarApto: async (usuarioId, apto) => {
    const response = await apiClient.patch(`/usuarios/${usuarioId}/apto?apto=${apto}`);
    return response.data;
  },

  // Actualizar usuario por DNI (PUT /api/usuarios/dni/{dni})
  updateUsuarioPorDni: async (dni, data) => {
    const response = await apiClient.put(`/usuarios/dni/${dni}`, data);
    return response.data;
  },

  // Buscar usuario por DNI
  getUsuarioPorDni: async (dni) => {
    const todosLosUsuarios = await usuarioService.getAllUsuarios();
    return todosLosUsuarios.find(u => u.dni === dni);
  },

  // Actualizar múltiples usuarios por DNI y asignarlos a un proceso
  updateUsuariosMasivoPorDni: async (usuarios, procesoId, supervisorId) => {
    // Importamos procesoService dinámicamente para evitar dependencia circular
    const { default: procesoService } = await import('./procesoService');
    
    const resultados = {
      exitosos: [],
      fallidos: []
    };

    for (const usuario of usuarios) {
      try {
        // 1. Buscar el usuario por DNI para obtener su ID
        console.log(`🔍 Buscando usuario con DNI: ${usuario.dni}`);
        const usuarioExistente = await usuarioService.getUsuarioPorDni(usuario.dni);
        
        if (!usuarioExistente) {
          throw new Error(`Usuario con DNI ${usuario.dni} no encontrado en el sistema`);
        }
        
        console.log(`✅ Usuario encontrado: ID ${usuarioExistente.id}`);

        // 2. Actualizar datos del usuario (nombre, apellido, supervisor)
        const datosActualizar = {
          name: usuario.nombre || usuario.name || usuarioExistente.name,
          lastname: usuario.apellido || usuario.lastname || usuarioExistente.lastname,
          supervisorId: supervisorId
        };

        console.log(`🔄 Actualizando usuario ID ${usuarioExistente.id}:`, datosActualizar);
        
        await usuarioService.updateUsuarioPorDni(usuario.dni, datosActualizar);
        console.log('✅ Usuario actualizado');

        // 3. Asignar al proceso usando POST /procesos/{procesoId}/asignar-usuario/{usuarioId}
        console.log(`📌 Asignando usuario ID ${usuarioExistente.id} al proceso ${procesoId}...`);
        
        try {
          await procesoService.asignarUsuarioAProceso(procesoId, usuarioExistente.id);
          console.log('✅ Usuario asignado al proceso exitosamente');
        } catch (asignacionError) {
          // Si ya está asignado, no es un error crítico
          if (asignacionError.response?.status === 400 || 
              asignacionError.response?.data?.message?.includes('ya está asignado')) {
            console.log('ℹ️ Usuario ya estaba asignado al proceso');
          } else {
            throw asignacionError;
          }
        }
        
        resultados.exitosos.push({
          usuario: usuarioExistente,
          datos: usuario
        });
      } catch (error) {
        console.error(`❌ Error al procesar usuario con DNI ${usuario.dni}:`, error);
        resultados.fallidos.push({
          datos: usuario,
          error: error.response?.data?.message || error.response?.data || error.message
        });
      }
    }

    return resultados;
  },

  // Crear múltiples usuarios de forma masiva
  createUsuariosMasivo: async (usuarios) => {
    const resultados = {
      exitosos: [],
      fallidos: [],
      actualizados: [] // Usuarios que ya existían y fueron actualizados
    };

    for (const usuario of usuarios) {
      try {
        const usuarioCreado = await usuarioService.createUsuario(usuario);
        console.log('🔍 Usuario creado (respuesta del backend):', usuarioCreado);
        
        // El backend retorna {message, rol} pero NO el ID
        // Debemos buscar el usuario recién creado por su username (DNI)
        let usuarioConId = null;
        
        // Si la respuesta tiene ID directamente
        if (usuarioCreado?.id) {
          usuarioConId = usuarioCreado;
        } else if (usuarioCreado?.user?.id) {
          usuarioConId = usuarioCreado.user;
        } else if (usuarioCreado?.usuario?.id) {
          usuarioConId = usuarioCreado.usuario;
        } else if (usuarioCreado?.data?.id) {
          usuarioConId = usuarioCreado.data;
        } else {
          // Si no hay ID en la respuesta, buscamos el usuario por DNI
          console.log(`🔍 Backend no retornó ID. Buscando usuario por DNI: ${usuario.dni}`);
          
          // Esperar 500ms para que el backend termine de guardar
          await new Promise(resolve => setTimeout(resolve, 500));
          
          try {
            // Obtener todos los usuarios y buscar por dni
            const todosLosUsuarios = await usuarioService.getAllUsuarios();
            console.log(`🔍 Total usuarios en sistema: ${todosLosUsuarios.length}`);
            console.log(`🔍 Buscando usuario con DNI: "${usuario.dni}"`);
            
            // Buscar por dni
            usuarioConId = todosLosUsuarios.find(u => 
              u.dni === usuario.dni
            );
            
            if (usuarioConId) {
              console.log(`✅ Usuario encontrado: ID ${usuarioConId.id}, DNI: ${usuarioConId.dni}, email: ${usuarioConId.email}`);
            } else {
              console.error(`❌ No se pudo encontrar el usuario con DNI: ${usuario.dni}`);
              console.error('🔍 Usuarios disponibles:', todosLosUsuarios.map(u => ({ id: u.id, dni: u.dni, email: u.email })));
            }
          } catch (busquedaError) {
            console.error('❌ Error al buscar usuario:', busquedaError);
          }
        }
        
        console.log('🔍 Usuario final con ID:', usuarioConId);
        console.log('🔍 ID del usuario:', usuarioConId?.id);
        
        resultados.exitosos.push({
          usuario: usuarioConId,
          datos: usuario
        });
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.response?.data || error.message || '';
        console.error('❌ Error al crear usuario:', errorMessage);
        
        // Si el error es "El DNI ya está registrado", actualizar el usuario existente
        if (errorMessage.includes('DNI ya está registrado') || errorMessage.includes('dni ya está registrado') || errorMessage.includes('DNI ya existe')) {
          console.log(`🔄 Usuario con DNI ${usuario.dni} ya existe. Actualizando datos...`);
          
          try {
            // Buscar el usuario existente por DNI
            const usuarioExistente = await usuarioService.getUsuarioPorDni(usuario.dni);
            
            if (usuarioExistente) {
              console.log(`✅ Usuario existente encontrado: ID ${usuarioExistente.id}`);
              
              // Actualizar datos del usuario con los nuevos del Excel
              const datosActualizar = {
                name: usuario.nombre || usuario.name || usuarioExistente.name,
                lastname: usuario.apellido || usuario.lastname || usuarioExistente.lastname,
                email: usuario.email || usuario.correo || usuarioExistente.email,
                supervisorId: usuario.supervisorId || usuarioExistente.supervisorId
              };
              
              console.log(`🔄 Actualizando usuario ID ${usuarioExistente.id} con:`, datosActualizar);
              
              await usuarioService.updateUsuarioPorDni(usuario.dni, datosActualizar);
              console.log(`✅ Usuario ${usuario.dni} actualizado exitosamente`);
              
              // Agregar a la lista de actualizados (se tratará como exitoso para la asignación)
              resultados.actualizados.push({
                usuario: usuarioExistente,
                datos: usuario
              });
              
              // También agregarlo a exitosos para que se asigne al proceso
              resultados.exitosos.push({
                usuario: usuarioExistente,
                datos: usuario
              });
            } else {
              console.error(`❌ No se encontró usuario con DNI ${usuario.dni} para actualizar`);
              resultados.fallidos.push({
                datos: usuario,
                error: 'Usuario ya existe pero no se pudo encontrar para actualizar'
              });
            }
          } catch (updateError) {
            console.error(`❌ Error al actualizar usuario existente ${usuario.dni}:`, updateError);
            resultados.fallidos.push({
              datos: usuario,
              error: `Error al actualizar: ${updateError.response?.data?.message || updateError.message}`
            });
          }
        } else {
          // Otro tipo de error, agregar a fallidos
          resultados.fallidos.push({
            datos: usuario,
            error: errorMessage
          });
        }
      }
    }

    return resultados;
  },
};

export default usuarioService;
